import StickyTableResponsive from '../components/common/StickyTableResponsive';
import SortableTh from '../components/common/SortableTh';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTableSort } from '../hooks/useTableSort';
import { Link, useParams, useNavigate } from 'react-router-dom';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import KkpPlanningPageActions from '../components/kkp/KkpPlanningPageActions';
import KkpPlanningEmptyState from '../components/kkp/KkpPlanningEmptyState';
import hebergementService from '../services/hebergementService';
import secteursService from '../services/secteursService';
import { notify } from '../components/common/AppToast';
import SecteursEntrepriseAutocomplete from '../components/secteurs/SecteursEntrepriseAutocomplete';
import SiteClientInlineCell from '../components/iti/SiteClientInlineCell';
import SiteCommercialInlineCell from '../components/iti/SiteCommercialInlineCell';
import SiteActivityInlineCell from '../components/iti/SiteActivityInlineCell';
import { ROUTES } from '../config/routes';
import { AppAlert, AppBadge, AppButton, AppStatusBadge } from '../components/ui';
import '../styles/kkp-planning-page.scss';

const PLUGIN_SORT_COLUMNS = {
  nom: { type: 'string', getValue: (row) => row.name },
  version: { type: 'string', getValue: (row) => row.version },
  statut: { type: 'string', getValue: (row) => (row.active ? 'actif' : 'inactif') },
};

const THEME_SORT_COLUMNS = {
  nom: { type: 'string', getValue: (row) => row.name },
  version: { type: 'string', getValue: (row) => row.version },
  statut: { type: 'string', getValue: (row) => (row.active ? 'actif' : 'inactif') },
};

const PluginTable = ({ plugins, showAll }) => {
  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(plugins, PLUGIN_SORT_COLUMNS);
  const displayed = showAll ? sortedRows : sortedRows.slice(0, 5);

  return (
    <>
      <StickyTableResponsive>
        <table className="table table-sm">
          <thead>
            <tr>
              <SortableTh sortKey="nom" label="Nom" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
              <SortableTh sortKey="version" label="Version" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
              <SortableTh sortKey="statut" label="Statut" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
            </tr>
          </thead>
          <tbody>
            {displayed.map((plugin, index) => (
              <tr key={index}>
                <td>
                  <div>
                    <strong>{plugin.name}</strong>
                    <br />
                    <small className="text-muted">{plugin.slug}</small>
                  </div>
                </td>
                <td>{plugin.version}</td>
                <td>
                  <AppStatusBadge
                    status={plugin.active ? 'active' : 'invalid'}
                    label={plugin.active ? 'Actif' : 'Inactif'}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </StickyTableResponsive>
      {!showAll && plugins.length > 5 && (
        <div className="text-center mt-2">
          <small className="text-muted">... et {plugins.length - 5} autres</small>
        </div>
      )}
    </>
  );
};

const ThemeTable = ({ themes, showAll }) => {
  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(themes, THEME_SORT_COLUMNS);
  const displayed = showAll ? sortedRows : sortedRows.slice(0, 3);

  return (
    <>
      <StickyTableResponsive>
        <table className="table table-sm">
          <thead>
            <tr>
              <SortableTh sortKey="nom" label="Nom" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
              <SortableTh sortKey="version" label="Version" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
              <SortableTh sortKey="statut" label="Statut" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
            </tr>
          </thead>
          <tbody>
            {displayed.map((theme, index) => (
              <tr key={index}>
                <td>
                  <div>
                    <strong>{theme.name}</strong>
                    <br />
                    <small className="text-muted">{theme.slug}</small>
                  </div>
                </td>
                <td>{theme.version}</td>
                <td>
                  <AppStatusBadge
                    status={theme.active ? 'active' : 'invalid'}
                    label={theme.active ? 'Actif' : 'Inactif'}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </StickyTableResponsive>
      {!showAll && themes.length > 3 && (
        <div className="text-center mt-2">
          <small className="text-muted">... et {themes.length - 3} autres</small>
        </div>
      )}
    </>
  );
};

const SiteDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiWorking, setApiWorking] = useState(true);
  const [showAllPlugins, setShowAllPlugins] = useState(false);
  const [showAllThemes, setShowAllThemes] = useState(false);
  const [secteursActiviteGroups, setSecteursActiviteGroups] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [linkSaving, setLinkSaving] = useState(false);
  const [autoLinking, setAutoLinking] = useState(false);
  const [selectedEntreprise, setSelectedEntreprise] = useState(null);

  // Fonction pour formater la date
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Mapping des IP vers les noms de serveurs
  const getServerName = (ip) => {
    const serverMapping = {
      '151.80.20.230': 's1.iti-conseil.com',
      '188.165.192.156': 's2.iti-conseil.com',
      '51.210.221.102': 's4.iti-conseil.com',
      '146.59.148.37': 's5.iti-conseil.com'
    };
    return serverMapping[ip] || ip;
  };

  const isSiteActive = (site) => site.hosted === true;

  const getSiteStatus = (site) => {
    if (site.hosted === true) return 'active';
    if (site.hosted === false) return 'inactive';
    return 'unknown';
  };

  // Fonction pour obtenir le texte du statut
  const getStatusText = (status) => {
    const statusTexts = {
      active: 'Hébergé',
      inactive: 'Plus hébergé',
      error: 'Erreur',
      unknown: 'Inconnu',
    };
    return statusTexts[status] || status;
  };

  const getSiteStatusBadge = (status) => {
    if (status === 'active') {
      return <AppStatusBadge status="active" label="Hébergé" />;
    }
    if (status === 'inactive') {
      return <AppStatusBadge status="invalid" label="Plus hébergé" />;
    }
    if (status === 'error') {
      return <AppBadge variant="warning">Erreur</AppBadge>;
    }
    return <AppBadge variant="neutral">Inconnu</AppBadge>;
  };

  const formatBytes = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const parseJsonData = (jsonString) => {
    if (!jsonString) return [];
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      return [];
    }
  };

  // Calculer les informations dérivées
  const siteInfo = useMemo(() => {
    if (!site) return null;

    const status = getSiteStatus(site);
    
    return {
      ...site,
      status,
      statusText: getStatusText(status),
      serverName: getServerName(site.ip),
      isActive: isSiteActive(site)
    };
  }, [site]);

  const fetchSiteDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await hebergementService.getSiteById(id);
      if (response.success) {
        setSite(response.data);
        setApiWorking(true);
        if (response.data.entrepriseLocaleId) {
          setSelectedEntreprise({
            id: response.data.entrepriseLocaleId,
            nom: response.data.entrepriseNom ?? '',
          });
        } else {
          setSelectedEntreprise(null);
        }
      } else {
        setError('Site non trouvé');
      }
    } catch (err) {
      console.error('Erreur lors du chargement du site:', err);
      setError(err.message || 'Erreur lors du chargement des détails du site');
      setApiWorking(false);
      
      // Données de test en cas d'erreur
      const testSite = {
        id: parseInt(id),
        mainwpId: `test-${id}`,
        name: `Site Test ${id}`,
        url: `https://test${id}.example.com`,
        ip: '151.80.20.230',
        status: 'active',
        httpStatus: 200,
        wpVersion: '6.4.2',
        phpVersion: '8.1.25',
        mysqlVersion: '8.0.35',
        healthStatus: 1,
        healthScore: 95,
        lastSync: '2024-01-15T10:30:00Z',
        memoryLimit: '256M',
        databaseSize: 52428800,
        activeTheme: 'Twenty Twenty-Four',
        sslVersion: 'TLS 1.3',
        isStaging: false,
        clientId: 'client-123',
        syncErrors: 'Aucune',
        plugins: '[]',
        themes: '[]',
        recentPages: '[]',
        recentComments: '[]',
        categories: '[]'
      };
      setSite(testSite);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchSiteDetails();
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      secteursService.getFilterOptions(),
      secteursService.getCommerciaux(),
    ])
      .then(([filterData, commerciauxData]) => {
        if (!cancelled) {
          setSecteursActiviteGroups(filterData.secteursActivite ?? []);
          setCommerciaux(commerciauxData ?? []);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleSiteUpdated = (_siteId, updatedSite) => {
    setSite(updatedSite);
    if (updatedSite.entrepriseLocaleId) {
      setSelectedEntreprise({
        id: updatedSite.entrepriseLocaleId,
        nom: updatedSite.entrepriseNom ?? '',
      });
    }
  };

  const handleSaveEntrepriseLink = async () => {
    if (!site?.id) return;
    setLinkSaving(true);
    try {
      const response = await hebergementService.linkEntreprise(
        site.id,
        selectedEntreprise?.id ?? null,
      );
      if (response.success) {
        setSite(response.data);
        notify.success(selectedEntreprise?.id
          ? 'Entreprise liée au site'
          : 'Liaison entreprise supprimée');
      }
    } catch (err) {
      notify.error(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLinkSaving(false);
    }
  };

  const handleAutoLinkEntreprise = async () => {
    if (!site?.id) return;
    setAutoLinking(true);
    try {
      const response = await hebergementService.autoLinkEntreprise(site.id, false);
      if (response.success) {
        setSite(response.data);
        if (response.linked) {
          setSelectedEntreprise({
            id: response.data.entrepriseLocaleId,
            nom: response.data.entrepriseNom ?? '',
          });
        }
        if (response.linked) {
          notify.success(response.message || 'Liaisons mises à jour');
        } else {
          notify.info('Aucune correspondance trouvée pour ce site');
        }
      }
    } catch (err) {
      notify.error(err.message || 'Erreur lors de l\'auto-liaison');
    } finally {
      setAutoLinking(false);
    }
  };

  const backToList = () => navigate(ROUTES.iti.hebergementSites);

  const pageActions = [
    ...(siteInfo?.url ? [{
      key: 'visit',
      label: 'Visiter le site',
      icon: 'mdi:open-in-new',
      onClick: () => window.open(siteInfo.url, '_blank'),
      variant: 'primary',
    }] : []),
    {
      key: 'back',
      label: 'Retour à la liste',
      icon: 'mdi:arrow-left',
      onClick: backToList,
    },
  ];

  const toolsMenu = <KkpPlanningPageActions actions={pageActions} />;
  const backOnlyActions = (
    <KkpPlanningPageActions
      actions={[
        {
          key: 'back',
          label: 'Retour à la liste',
          icon: 'mdi:arrow-left',
          onClick: backToList,
        },
      ]}
    />
  );

  if (loading) {
    return (
      <MasterLayout>
        <Breadcrumb
          title="Site web"
          subtitle="Détail site web"
          actions={backOnlyActions}
        />
        <KkpPlanningEmptyState loading />
      </MasterLayout>
    );
  }

  if (error && !site) {
    return (
      <MasterLayout>
        <Breadcrumb
          title="Site web"
          subtitle="Détail site web"
          actions={backOnlyActions}
        />
        <KkpPlanningEmptyState
          message={error}
          actions={[
            {
              key: 'back',
              label: 'Retour à la liste',
              icon: 'mdi:arrow-left',
              onClick: backToList,
              variant: 'primary',
            },
          ]}
        />
      </MasterLayout>
    );
  }

  if (!siteInfo) {
    return (
      <MasterLayout>
        <Breadcrumb
          title="Site web"
          subtitle="Détail site web"
          actions={backOnlyActions}
        />
        <KkpPlanningEmptyState
          message="Site non trouvé"
          actions={[
            {
              key: 'back',
              label: 'Retour à la liste',
              icon: 'mdi:arrow-left',
              onClick: backToList,
              variant: 'primary',
            },
          ]}
        />
      </MasterLayout>
    );
  }

  const plugins = parseJsonData(siteInfo.plugins);
  const themes = parseJsonData(siteInfo.themes);
  const recentPages = parseJsonData(siteInfo.recentPages);
  const recentComments = parseJsonData(siteInfo.recentComments);
  const categories = parseJsonData(siteInfo.categories);

  return (
    <MasterLayout>
      <Breadcrumb
        title={siteInfo.name}
        subtitle={
          <>
            Détail site web
            {siteInfo.url && (
              <>
                {' · '}
                <a href={siteInfo.url} target="_blank" rel="noopener noreferrer" className="text-primary">
                  {siteInfo.url}
                </a>
              </>
            )}
            {!apiWorking && (
              <AppBadge variant="warning" className="ms-2">Mode test</AppBadge>
            )}
          </>
        }
        actions={toolsMenu}
      />

      {error && (
        <AppAlert variant="warning" className="py-2">
          <strong>Mode test :</strong> {error}
        </AppAlert>
      )}

      <div className="row">
          {/* Informations générales */}
          <div className="col-lg-8">
            <div className="card mb-4">
              <div className="card-header py-2">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-information-line me-2"></i>
                  Informations générales
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Nom du site</label>
                      <p className="mb-0">{siteInfo.name}</p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">URL</label>
                      <p className="mb-0">
                        <a href={siteInfo.url} target="_blank" rel="noopener noreferrer" className="text-primary">
                          {siteInfo.url}
                        </a>
                      </p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Statut</label>
                      <div>
                        {getSiteStatusBadge(siteInfo.status)}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Serveur</label>
                      <p className="mb-0">{siteInfo.serverName}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-bold">ID MainWP</label>
                      <p className="mb-0">{siteInfo.mainwpId}</p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">IP</label>
                      <p className="mb-0">{siteInfo.ip}</p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Client ID</label>
                      <p className="mb-0">{siteInfo.clientId || 'N/A'}</p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Staging</label>
                      <div>
                        <AppBadge variant={siteInfo.isStaging ? 'warning' : 'success'}>
                          {siteInfo.isStaging ? 'Oui' : 'Non'}
                        </AppBadge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Informations techniques */}
            <div className="card mb-4">
              <div className="card-header py-2">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-settings-3-line me-2"></i>
                  Informations techniques
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-bold">WordPress</label>
                      <p className="mb-0">
                        <AppBadge variant="info">
                          {siteInfo.wpVersion || 'N/A'}
                        </AppBadge>
                      </p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">PHP</label>
                      <p className="mb-0">
                        <AppBadge variant="success">
                          {siteInfo.phpVersion || 'N/A'}
                        </AppBadge>
                      </p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">MySQL</label>
                      <p className="mb-0">{siteInfo.mysqlVersion || 'N/A'}</p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Thème actif</label>
                      <p className="mb-0">{siteInfo.activeTheme || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Mémoire limit</label>
                      <p className="mb-0">{siteInfo.memoryLimit || 'N/A'}</p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Taille DB</label>
                      <p className="mb-0">{formatBytes(siteInfo.databaseSize)}</p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">SSL Version</label>
                      <p className="mb-0">{siteInfo.sslVersion || 'N/A'}</p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Score de santé</label>
                      <p className="mb-0">
                        <AppBadge variant="primary">
                          {siteInfo.healthScore || 'N/A'}
                        </AppBadge>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Informations de synchronisation */}
            <div className="card mb-4">
              <div className="card-header py-2">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-refresh-line me-2"></i>
                  Informations de synchronisation
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Dernière synchronisation</label>
                      <p className="mb-0">
                        {formatDate(siteInfo.lastSync)}
                      </p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">HTTP Status</label>
                      <p className="mb-0">
                        <AppBadge variant={siteInfo.httpStatus === 200 ? 'success' : 'danger'}>
                          {siteInfo.httpStatus || 'N/A'}
                        </AppBadge>
                        <small className="text-muted ms-2">
                          {siteInfo.httpStatus === 200 ? '(Site accessible)' : 
                           siteInfo.httpStatus >= 400 ? '(Erreur serveur)' : 
                           siteInfo.httpStatus >= 300 ? '(Redirection)' : 
                           '(Statut inconnu)'}
                        </small>
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Erreurs de sync</label>
                      <p className="mb-0">{siteInfo.syncErrors || 'Aucune'}</p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">État de santé</label>
                      <p className="mb-0">
                        <AppStatusBadge
                          status={siteInfo.healthStatus === 1 ? 'active' : 'invalid'}
                          label={siteInfo.healthStatus === 1 ? 'En bonne santé' : 'Problème de santé'}
                        />
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Informations complémentaires */}
          <div className="col-lg-4">
            <div className="card mb-4">
              <div className="card-header py-2">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-building-line me-2"></i>
                  Client, commercial &amp; activité
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">Client Dolibarr</label>
                  <SiteClientInlineCell
                    siteInfo={siteInfo}
                    apiWorking={apiWorking}
                    onUpdated={handleSiteUpdated}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Commercial</label>
                  <SiteCommercialInlineCell
                    siteInfo={siteInfo}
                    commerciaux={commerciaux}
                    apiWorking={apiWorking}
                    onUpdated={handleSiteUpdated}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Secteur d&apos;activité</label>
                  <SiteActivityInlineCell
                    siteInfo={siteInfo}
                    secteursActiviteGroups={secteursActiviteGroups}
                    apiWorking={apiWorking}
                    onUpdated={handleSiteUpdated}
                  />
                  {siteInfo.sectionActiviteNafLabel && (
                    <small className="text-muted d-block mt-1">
                      NAF INSEE : {siteInfo.sectionActiviteNaf} — {siteInfo.sectionActiviteNafLabel}
                    </small>
                  )}
                  {siteInfo.activitePrincipale && (
                    <small className="text-muted d-block mt-1">
                      Code NAF {siteInfo.activitePrincipale}
                    </small>
                  )}
                </div>

                {siteInfo.entrepriseLocaleId && (
                  <div className="mb-3">
                    <label className="form-label fw-bold">Entreprise liée</label>
                    <p className="mb-1">{siteInfo.entrepriseNom}</p>
                    {siteInfo.entrepriseSiren && (
                      <small className="text-muted">SIREN {siteInfo.entrepriseSiren}</small>
                    )}
                    <div className="mt-2">
                      <AppButton
                        as={Link}
                        to={ROUTES.secteurs.entrepriseDetail(siteInfo.entrepriseLocaleId)}
                        size="sm"
                        variant="outline-primary-600"
                      >
                        Voir la fiche entreprise
                      </AppButton>
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label fw-bold">Associer une entreprise</label>
                  <SecteursEntrepriseAutocomplete
                    value={selectedEntreprise?.nom ?? ''}
                    onSelect={(entreprise) => setSelectedEntreprise(entreprise.id ? entreprise : null)}
                    disabled={linkSaving || autoLinking}
                  />
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <AppButton
                    size="sm"
                    variant="success"
                    onClick={handleSaveEntrepriseLink}
                    disabled={linkSaving || autoLinking || !apiWorking}
                    loading={linkSaving}
                    loadingLabel="Enregistrement…"
                  >
                    Enregistrer
                  </AppButton>
                  <AppButton
                    size="sm"
                    variant="outline-secondary"
                    onClick={handleAutoLinkEntreprise}
                    disabled={linkSaving || autoLinking || !apiWorking}
                    loading={autoLinking}
                    loadingLabel="Recherche…"
                    title="clientId → Dolibarr → entreprise SIRENE + commercial (représentant ou vente KKP)"
                  >
                    Auto-lier client / entreprise / commercial
                  </AppButton>
                  {siteInfo.entrepriseLocaleId && (
                    <AppButton
                      size="sm"
                      variant="outline-danger"
                      onClick={() => {
                        setSelectedEntreprise(null);
                        hebergementService.linkEntreprise(site.id, null)
                          .then((response) => {
                            if (response.success) {
                              setSite(response.data);
                              notify.success('Liaison supprimée');
                            }
                          })
                          .catch((err) => notify.error(err.message));
                      }}
                      disabled={linkSaving || autoLinking}
                    >
                      Dissocier
                    </AppButton>
                  )}
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header py-2 d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-plug-line me-2"></i>
                  Plugins ({plugins.length})
                </h5>
                {plugins.length > 5 && (
                  <AppButton
                    size="sm"
                    variant="outline-primary"
                    onClick={() => setShowAllPlugins(!showAllPlugins)}
                  >
                    {showAllPlugins ? 'Voir moins' : 'Voir tous'}
                  </AppButton>
                )}
              </div>
              <div className="card-body">
                {plugins.length > 0 ? (
                  <PluginTable plugins={plugins} showAll={showAllPlugins} />
                ) : (
                  <p className="text-muted">Aucun plugin trouvé</p>
                )}
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header py-2 d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-palette-line me-2"></i>
                  Thèmes ({themes.length})
                </h5>
                {themes.length > 3 && (
                  <AppButton
                    size="sm"
                    variant="outline-primary"
                    onClick={() => setShowAllThemes(!showAllThemes)}
                  >
                    {showAllThemes ? 'Voir moins' : 'Voir tous'}
                  </AppButton>
                )}
              </div>
              <div className="card-body">
                {themes.length > 0 ? (
                  <ThemeTable themes={themes} showAll={showAllThemes} />
                ) : (
                  <p className="text-muted">Aucun thème trouvé</p>
                )}
              </div>
            </div>
          </div>
        </div>
    </MasterLayout>
  );
};

export default SiteDetailPage;